<template>
  <ModalComponent
    v-model="modalVisible"
    class="sablier-devant"
  >
    <template #header>
      <template v-if="mode === 'contact'">Ajouter une entité juridique</template>
      <template v-else>Ajouter un utilisateur</template>
    </template>
    <template #content>
      <div class="flex flex-column flex-gap-4">
        <BasicAutocomplete
          v-if="mode === 'entite'"
          v-model="contact"
          :search="searchContactInt"
          :search-min-length="3"
          key-id="userId"
          key-label="email"
          placeholder="Nom, prénom, email"
          class="flex-1"
          @update:lastSearchResultCount="lastSearchCount = $event"
        >
          <template #default="item">
            <div
              class="flex-1 flex flex-column"
              :class="store.diagContact(item) ? 'item-disabled' : ''"
            >
              <template v-if="item.username && !item.email?.startsWith(item.username)">
                <div class="flex-1">
                  <BasicText class="flex-1">{{ item.username }}</BasicText>
                </div>
                <div class="flex-1">
                  <BasicText
                    size="body-s"
                    class="flex-1"
                  >
                    {{ item.email }}
                  </BasicText>
                </div>
              </template>
              <div
                v-else
                class="flex-1"
              >
                <BasicText class="flex-1">{{ item.email }}</BasicText>
              </div>
            </div>
          </template>
        </BasicAutocomplete>
        <BasicAutocomplete
          v-else
          v-model="entite"
          :search="searchEntiteInt"
          :search-min-length="estModeEntiteSirenSiret ? 9 : 1"
          key-id="identifiant"
          :get-input-text="getTextEntite"
          :placeholder="estModeEntiteSirenSiret ? 'Siren ou Siret' : 'Raison sociale, siren, siret'"
          class="flex-1"
          strict
          :search-formater="sirenSiretSearchFormater"
        >
          <template #default="item">
            <div
              class="flex-1"
              :class="store.diagEntite(item) ? 'item-disabled' : ''"
            >
              <div class="flex">
                <BasicText class="flex-1">{{ item.denomination }}</BasicText>
                <BasicTag
                  v-if="!item.nic"
                  label="Entreprise"
                  size="medium"
                  class="tag--danger"
                />
              </div>
              <div class="flex flex-gap-1">
                <BasicText
                  v-if="item.nic"
                  size="body-s"
                >
                  Siret :
                </BasicText>
                <BasicText
                  v-else
                  size="body-s"
                >
                  Siren :
                </BasicText>
                <BasicText
                  size="body-s"
                  weight="bold"
                >
                  {{ item.siren }} {{ item.nic }}
                </BasicText>
              </div>
            </div>
          </template>
        </BasicAutocomplete>
        <BasicAlert
          v-if="diagAjout === 'doublon'"
          alert-type="danger"
          alert-label="Ce rattachement existe déjà. Il ne peut être ajouté."
        />
        <BasicAlert
          v-else-if="diagAjout === 'entreprise'"
          alert-type="danger"
          alert-label="Cet utilisateur est déjà rattaché à l'entreprise. Ce rattachement ne peut être ajouté."
        />
        <BasicAlert
          v-else-if="diagAjout === 'etablissement'"
          alert-type="danger"
          alert-label="Cet utilisateur est déjà rattaché à un ou des établissements de cette entreprise. Ce rattachement ne peut être ajouté."
        />
        <template v-else>
          <BasicAlert
            v-if="mode === 'contact'"
            alert-type="info"
            alert-label="Sélectionnez l'entité juridique à rattacher."
          />
          <BasicAlert
            v-else
            alert-type="info"
            alert-label="Sélectionnez ou saisissez l'utilisateur à rattacher."
          />
        </template>
      </div>
    </template>
    <template #actions>
      <div class="flex flex-gap-4">
        <BasicButton
          label="Annuler"
          @click="store.setCancel"
          class="flex-1"
        />
        <BasicButton
          label="Ajouter"
          :disabled="!ajoutAutorisé"
          @click="store.ajouterHabilitation((contact ?? entite)!)"
          class="flex-1"
        />
      </div>
    </template>
  </ModalComponent>
</template>
<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import ModalComponent from '@/features/interface/modal/ModalComponent.vue'
  import { sirenSiretSearchFormater } from '@/features/interface/shared/tools'
  import type { ContactDTO } from '@/features/api/habilitation/dto/contact/ContactDTO'
  import type { Entite } from './useHabilitationsAdminEngineStore'
  import { useHabilitationAddStore, type DiagAjout } from './useHabilitationAdd'
  import { storeToRefs } from 'pinia'
  import { isSiren, isSiret } from '@/features/shared/tools/luhn'
  import type { AutocompleteLastSearchCount } from '@designSystem/components'

  const store = useHabilitationAddStore()
  const { visible, mode, estModeEntiteSirenSiret } = storeToRefs(store)

  const regexEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/i
  const entite = ref<Entite>()
  const contact = ref<ContactDTO>()

  const lastSearchCount = ref<AutocompleteLastSearchCount>(0)

  const modalVisible = defineModel<boolean>({
    get: () => store.visible,
    set: (value) => {
      if (!value) {
        store.setCancel()
      }
    },
  })

  watch(visible, () => {
    contact.value = undefined
    entite.value = undefined
  })

  function searchBuilder<T>(
    searchFun: (search: string, signal: AbortSignal) => Promise<T[]>,
  ): (search: string, signal: AbortSignal) => Promise<T[]> {
    return (search: string, signal: AbortSignal) => {
      search = search.trim()
      if (search.length === 0) return Promise.resolve([])
      return searchFun(search, signal)
    }
  }

  const searchEntiteInt = searchBuilder((search, signal) => {
    if (estModeEntiteSirenSiret.value) {
      if (!regexEmail.test(search)) return Promise.resolve([])
      if (search.length === 9 && !isSiren(search)) return Promise.resolve([])
      if (search.length === 14 && !isSiret(search)) return Promise.resolve([])
    }
    return store.searchEntite(search, signal)
  })

  const searchContactInt = searchBuilder(store.searchContact)

  const ajoutAutorisé = computed(() => {
    return estSaisieTerminée.value && diagAjout.value === undefined
  })

  const estSaisieTerminée = computed<boolean>(() => {
    if (typeof lastSearchCount.value === 'string') return false
    if (mode.value === 'entite') {
      if (contact.value?.userId) return true
      if (regexEmail.test(contact.value?.email ?? '') && lastSearchCount.value === 0) return true
    } else if (mode.value === 'contact') {
      if (entite.value?.identifiant) return true
    }
    return false
  })

  const diagAjout = computed<DiagAjout | undefined>(() => {
    if (!estSaisieTerminée.value) return
    if (mode.value === 'entite' && contact.value) {
      return store.diagContact(contact.value!)
    } else if (mode.value === 'contact' && entite.value) {
      return store.diagEntite(entite.value!)
    }
    return undefined
  })

  const getTextEntite = (value: Entite) => {
    if (value.nic) return value.denomination
    return `${value.denomination} - Entreprise`
  }
</script>

<style lang="less" scoped>
  .item-disabled {
    color: rgb(166, 166, 166);
    cursor: not-allowed;
  }
</style>
